import { useState } from 'react'
import { useStory } from '../contexts/StoryContext'
import './AICreator.css'

// Bilingual content - English and Spanish
const CONTENT = {
  en: {
    characters: [
      { id: 'rabbit', label: '🐰 Brave Rabbit', type: 'rabbit' },
      { id: 'fox', label: '🦊 Clever Fox', type: 'fox' },
      { id: 'dragon', label: '🐉 Kind Dragon', type: 'dragon' },
      { id: 'robot', label: '🤖 Curious Robot', type: 'robot' },
      { id: 'custom', label: '✨ Custom...', type: 'custom' }
    ],
    traits: [
      { id: 'brave', label: '💪 Brave', value: 'brave' },
      { id: 'funny', label: '😄 Funny', value: 'funny' },
      { id: 'shy', label: '🙈 Shy', value: 'shy' },
      { id: 'smart', label: '🧠 Smart', value: 'smart' },
      { id: 'kind', label: '💖 Kind', value: 'kind' }
    ],
    settings: [
      { id: 'forest', label: '🌲 Magical Forest', icon: '🌲', value: 'magical forest' },
      { id: 'underwater', label: '🌊 Underwater Kingdom', icon: '🌊', value: 'underwater kingdom' },
      { id: 'space', label: '🚀 Space Station', icon: '🚀', value: 'space station' },
      { id: 'neighborhood', label: '🏘️ Your Neighborhood', icon: '🏘️', value: 'your neighborhood' },
      { id: 'castle', label: '🏰 Castle', icon: '🏰', value: 'castle' }
    ],
    conflicts: [
      { id: 'lost', label: '🔍 Lost something important', value: 'lost something important' },
      { id: 'save', label: '🦸 Need to save a friend', value: 'needs to save a friend' },
      { id: 'mystery', label: '🕵️ Discover a mystery', value: 'discovers a mystery' },
      { id: 'learn', label: '📚 Learn something new', value: 'learns something new' }
    ],
    tones: [
      { id: 'funny', label: '😂', name: 'Funny' },
      { id: 'scary', label: '😱', name: 'A little scary' },
      { id: 'exciting', label: '🤩', name: 'Exciting' },
      { id: 'thoughtful', label: '🤔', name: 'Thoughtful' }
    ],
    ui: {
      createYourStory: 'Create Your Story',
      back: '← Back',
      next: 'Next →',
      generating: '✨ Creating...',
      generate: '🚀 Generate Story!',
      whosYourHero: "🌟 Who's your hero?",
      customPlaceholder: 'What kind of character?',
      namePlaceholder: "What's their name?",
      pickTraits: 'Pick 1-2 traits:',
      whereAdventure: '🗺️ Where does the adventure happen?',
      whatProblem: '⚡ What problem do they face?',
      howFeeling: '🎭 How should it feel?',
      yourPrompt: '✨ Your Prompt',
      youPicked: 'You picked:',
      teachingMoment1: '💡 See how your choices help the AI know what story to write?',
      teachingMoment2: 'This is called a',
      prompt: 'prompt',
      teachingMoment3: 'The clearer you are, the better story you\'ll get.',
      error: "Oops! We couldn't create your story. Let's try again!",
      promptTemplate: (traits, charType, name, setting, conflict, tone) => 
        `A ${traits} ${charType} named ${name}, in a ${setting}, who ${conflict}, and you want it to be ${tone}!`
    },
    fallback: {
      lookAround: 'Look around carefully',
      callHelp: 'Call out for help',
      followFootprints: 'Follow the footprints',
      checkShiny: 'Check the shiny thing',
      makeFriend: 'Make a new friend',
      exploreCleaning: 'Explore the clearing',
      acceptHelp: 'Accept their help',
      goAlone: 'Thank them but go alone',
      intro: (name, type, setting, conflict) => 
        `You are ${name}, a ${type}. You find yourself in a ${setting}. Something important has happened - ${conflict}. What do you do?`,
      lookText: 'You take a careful look around. You notice something shiny in the distance and footprints leading to a hidden path.',
      callText: 'You call out, and a friendly creature appears! They offer to help you on your quest.',
      ending1: 'Your bravery paid off! You solved the mystery and made a new friend along the way. The End!',
      ending2: 'The shiny thing was a magical key! It opened a treasure chest full of wonderful surprises. The End!',
      ending3: 'Working together, you and your new friend accomplished what seemed impossible. The End!',
      achievement1: 'Brave Explorer',
      achievement2: 'Treasure Finder',
      achievement3: 'Team Player'
    }
  },
  es: {
    characters: [
      { id: 'rabbit', label: '🐰 Conejo Valiente', type: 'conejo' },
      { id: 'fox', label: '🦊 Zorro Astuto', type: 'zorro' },
      { id: 'dragon', label: '🐉 Dragón Bondadoso', type: 'dragón' },
      { id: 'robot', label: '🤖 Robot Curioso', type: 'robot' },
      { id: 'custom', label: '✨ Personalizado...', type: 'custom' }
    ],
    traits: [
      { id: 'brave', label: '💪 Valiente', value: 'valiente' },
      { id: 'funny', label: '😄 Gracioso', value: 'gracioso' },
      { id: 'shy', label: '🙈 Tímido', value: 'tímido' },
      { id: 'smart', label: '🧠 Inteligente', value: 'inteligente' },
      { id: 'kind', label: '💖 Amable', value: 'amable' }
    ],
    settings: [
      { id: 'forest', label: '🌲 Bosque Mágico', icon: '🌲', value: 'bosque mágico' },
      { id: 'underwater', label: '🌊 Reino Submarino', icon: '🌊', value: 'reino submarino' },
      { id: 'space', label: '🚀 Estación Espacial', icon: '🚀', value: 'estación espacial' },
      { id: 'neighborhood', label: '🏘️ Tu Vecindario', icon: '🏘️', value: 'tu vecindario' },
      { id: 'castle', label: '🏰 Castillo', icon: '🏰', value: 'castillo' }
    ],
    conflicts: [
      { id: 'lost', label: '🔍 Perdió algo importante', value: 'perdió algo importante' },
      { id: 'save', label: '🦸 Necesita salvar a un amigo', value: 'necesita salvar a un amigo' },
      { id: 'mystery', label: '🕵️ Descubre un misterio', value: 'descubre un misterio' },
      { id: 'learn', label: '📚 Aprende algo nuevo', value: 'aprende algo nuevo' }
    ],
    tones: [
      { id: 'funny', label: '😂', name: 'Gracioso' },
      { id: 'scary', label: '😱', name: 'Un poco de miedo' },
      { id: 'exciting', label: '🤩', name: 'Emocionante' },
      { id: 'thoughtful', label: '🤔', name: 'Reflexivo' }
    ],
    ui: {
      createYourStory: 'Crea Tu Historia',
      back: '← Atrás',
      next: 'Siguiente →',
      generating: '✨ Creando...',
      generate: '🚀 ¡Generar Historia!',
      whosYourHero: '🌟 ¿Quién es tu héroe?',
      customPlaceholder: '¿Qué tipo de personaje?',
      namePlaceholder: '¿Cómo se llama?',
      pickTraits: 'Elige 1-2 características:',
      whereAdventure: '🗺️ ¿Dónde ocurre la aventura?',
      whatProblem: '⚡ ¿Qué problema enfrenta?',
      howFeeling: '🎭 ¿Cómo debe sentirse?',
      yourPrompt: '✨ Tu Prompt',
      youPicked: 'Elegiste:',
      teachingMoment1: '💡 ¿Ves cómo tus elecciones ayudan a la IA a saber qué historia escribir?',
      teachingMoment2: 'Esto se llama un',
      prompt: 'prompt',
      teachingMoment3: 'Entre más claro seas, ¡mejor será tu historia!',
      error: '¡Ups! No pudimos crear tu historia. ¡Intentemos de nuevo!',
      promptTemplate: (traits, charType, name, setting, conflict, tone) => 
        `Un ${charType} ${traits} llamado ${name}, en un ${setting}, que ${conflict}, ¡y quieres que sea ${tone}!`
    },
    fallback: {
      lookAround: 'Mirar alrededor con cuidado',
      callHelp: 'Pedir ayuda',
      followFootprints: 'Seguir las huellas',
      checkShiny: 'Revisar lo brillante',
      makeFriend: 'Hacer un nuevo amigo',
      exploreCleaning: 'Explorar el claro',
      acceptHelp: 'Aceptar su ayuda',
      goAlone: 'Agradecer pero ir solo',
      intro: (name, type, setting, conflict) => 
        `Eres ${name}, un ${type}. Te encuentras en un ${setting}. Algo importante ha pasado - ${conflict}. ¿Qué haces?`,
      lookText: 'Miras con cuidado a tu alrededor. Notas algo brillante a lo lejos y huellas que llevan a un camino escondido.',
      callText: '¡Pides ayuda y aparece una criatura amigable! Te ofrece ayudarte en tu aventura.',
      ending1: '¡Tu valentía dio frutos! Resolviste el misterio e hiciste un nuevo amigo. ¡Fin!',
      ending2: '¡Lo brillante era una llave mágica! Abrió un cofre del tesoro lleno de sorpresas maravillosas. ¡Fin!',
      ending3: '¡Trabajando juntos, tú y tu nuevo amigo lograron lo que parecía imposible! ¡Fin!',
      achievement1: 'Explorador Valiente',
      achievement2: 'Buscador de Tesoros',
      achievement3: 'Jugador de Equipo'
    }
  }
}

const STEPS = ['character', 'setting', 'conflict', 'tone', 'review']

function AICreator({ onBack, onStoryGenerated }) {
  const { userSettings, loadStory, saveGeneratedStory } = useStory()
  const [step, setStep] = useState(0)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState(null)
  
  // Get current language content
  const lang = userSettings.language || 'en'
  const content = CONTENT[lang] || CONTENT.en
  
  const [storyConfig, setStoryConfig] = useState({
    character: null,
    characterName: '',
    customCharacter: '',
    traits: [],
    setting: null,
    conflict: null,
    tone: null
  })

  const currentStep = STEPS[step]
  const canProceed = () => {
    switch (currentStep) {
      case 'character': return storyConfig.character && storyConfig.characterName
      case 'setting': return storyConfig.setting
      case 'conflict': return storyConfig.conflict
      case 'tone': return storyConfig.tone
      default: return true
    }
  }

  const buildPrompt = () => {
    const char = content.characters.find(c => c.id === storyConfig.character)
    const charType = storyConfig.character === 'custom' 
      ? storyConfig.customCharacter 
      : char?.type
    const setting = content.settings.find(s => s.id === storyConfig.setting)
    const conflict = content.conflicts.find(c => c.id === storyConfig.conflict)
    const tone = content.tones.find(t => t.id === storyConfig.tone)
    const traits = storyConfig.traits
      .map(t => content.traits.find(tr => tr.id === t)?.value)
      .filter(Boolean)
      .join(lang === 'es' ? ' y ' : ' and ') || (lang === 'es' ? 'valiente' : 'brave')

    // Build display text for the user
    const displayText = content.ui.promptTemplate(
      traits,
      charType,
      storyConfig.characterName,
      setting?.value,
      conflict?.value,
      tone?.name
    )

    // Build system prompt for the AI (always includes language instruction)
    const targetLanguage = lang === 'es' ? 'Spanish' : 'English'
    const ageRange = userSettings.ageGroup === 'little' ? '4-6' : userSettings.ageGroup === 'middle' ? '7-9' : '10-12'
    
    const systemPrompt = `You are a children's story writer creating a short interactive adventure.

CRITICAL: Write the ENTIRE story in ${targetLanguage}. All text, choices, and content must be in ${targetLanguage}.
        
STORY PARAMETERS:
- Hero: ${storyConfig.characterName}, a ${charType} who is ${traits}
- Setting: ${setting?.value}
- Problem: ${conflict?.value}
- Tone: ${tone?.name}
- Target age: ${ageRange}
- Language: ${targetLanguage} (MUST write everything in ${targetLanguage})
- Culture context: ${userSettings.culture || (lang === 'es' ? 'es-MX' : 'en-US')}

REQUIREMENTS:
- Write in second person ("You walk into..." / "Caminas hacia...")
- Keep vocabulary appropriate for ages ${ageRange}
- Include 2-3 choice points where the reader decides what happens
- Each choice should lead to meaningfully different outcomes
- End with a satisfying conclusion
- Total length: approximately 300 words
- ALL TEXT MUST BE IN ${targetLanguage.toUpperCase()}
- Return a valid JSON object with the story structure

Return ONLY valid JSON in this exact format:
{
  "title": "Story Title in ${targetLanguage}",
  "nodes": {
    "start": {
      "id": "start",
      "text": "Opening text in ${targetLanguage}...",
      "choices": [
        { "text": "Choice 1 in ${targetLanguage}", "next": "node2", "emotion": "brave" },
        { "text": "Choice 2 in ${targetLanguage}", "next": "node3", "emotion": "cautious" }
      ]
    }
  }
}`

    return {
      display: displayText,
      system: systemPrompt
    }
  }

  const generateStory = async () => {
    setIsGenerating(true)
    setError(null)

    const prompt = buildPrompt()

    try {
      const response = await fetch('/api/generate-story', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: prompt.system,
          userPrompt: prompt.display,
          language: lang  // Pass language to API for potential server-side use
        })
      })

      if (!response.ok) {
        throw new Error('Failed to generate story')
      }

      const story = await response.json()
      
      // Add metadata
      const fullStory = {
        ...story,
        storyId: `ai-${Date.now()}`,
        culture: userSettings.culture,
        language: lang,
        ageRange: userSettings.ageGroup,
        isGenerated: true,
        config: storyConfig
      }

      saveGeneratedStory(fullStory)
      loadStory(fullStory)
      onStoryGenerated()
    } catch (err) {
      console.error('Story generation error:', err)
      setError(content.ui.error)
      
      // Fallback: load a demo story in the correct language
      loadDemoStory()
    } finally {
      setIsGenerating(false)
    }
  }

  const loadDemoStory = () => {
    const fb = content.fallback
    const char = content.characters.find(c => c.id === storyConfig.character)
    const charType = storyConfig.character === 'custom' 
      ? storyConfig.customCharacter 
      : char?.type
    const setting = content.settings.find(s => s.id === storyConfig.setting)
    const conflict = content.conflicts.find(c => c.id === storyConfig.conflict)

    const demoStory = {
      storyId: `demo-${Date.now()}`,
      title: lang === 'es' 
        ? `La Aventura de ${storyConfig.characterName}` 
        : `${storyConfig.characterName}'s Adventure`,
      isGenerated: true,
      language: lang,
      nodes: {
        start: {
          id: 'start',
          text: fb.intro(storyConfig.characterName, charType, setting?.value, conflict?.value),
          choices: [
            { text: fb.lookAround, next: 'look', emotion: 'cautious' },
            { text: fb.callHelp, next: 'call', emotion: 'brave' }
          ]
        },
        look: {
          id: 'look',
          text: fb.lookText,
          choices: [
            { text: fb.followFootprints, next: 'ending1' },
            { text: fb.checkShiny, next: 'ending2' }
          ]
        },
        call: {
          id: 'call',
          text: fb.callText,
          choices: [
            { text: fb.acceptHelp, next: 'ending3' },
            { text: fb.goAlone, next: 'ending1' }
          ]
        },
        ending1: {
          id: 'ending1',
          text: fb.ending1,
          achievement: fb.achievement1,
          choices: []
        },
        ending2: {
          id: 'ending2',
          text: fb.ending2,
          achievement: fb.achievement2,
          choices: []
        },
        ending3: {
          id: 'ending3',
          text: fb.ending3,
          achievement: fb.achievement3,
          choices: []
        }
      }
    }
    loadStory(demoStory)
    onStoryGenerated()
  }

  const renderStep = () => {
    switch (currentStep) {
      case 'character':
        return (
          <div className="step-content">
            <h2>{content.ui.whosYourHero}</h2>
            <div className="options-grid">
              {content.characters.map(char => (
                <button
                  key={char.id}
                  className={`option-btn ${storyConfig.character === char.id ? 'selected' : ''}`}
                  onClick={() => setStoryConfig({ ...storyConfig, character: char.id })}
                >
                  {char.label}
                </button>
              ))}
            </div>
            {storyConfig.character === 'custom' && (
              <input
                type="text"
                placeholder={content.ui.customPlaceholder}
                value={storyConfig.customCharacter}
                onChange={(e) => setStoryConfig({ ...storyConfig, customCharacter: e.target.value })}
                className="text-input"
              />
            )}
            <input
              type="text"
              placeholder={content.ui.namePlaceholder}
              value={storyConfig.characterName}
              onChange={(e) => setStoryConfig({ ...storyConfig, characterName: e.target.value })}
              className="text-input name-input"
            />
            <h3>{content.ui.pickTraits}</h3>
            <div className="traits-grid">
              {content.traits.map(trait => (
                <button
                  key={trait.id}
                  className={`trait-btn ${storyConfig.traits.includes(trait.id) ? 'selected' : ''}`}
                  onClick={() => {
                    const traits = storyConfig.traits.includes(trait.id)
                      ? storyConfig.traits.filter(t => t !== trait.id)
                      : storyConfig.traits.length < 2
                        ? [...storyConfig.traits, trait.id]
                        : storyConfig.traits
                    setStoryConfig({ ...storyConfig, traits })
                  }}
                >
                  {trait.label}
                </button>
              ))}
            </div>
          </div>
        )

      case 'setting':
        return (
          <div className="step-content">
            <h2>{content.ui.whereAdventure}</h2>
            <div className="options-grid large">
              {content.settings.map(setting => (
                <button
                  key={setting.id}
                  className={`option-btn card ${storyConfig.setting === setting.id ? 'selected' : ''}`}
                  onClick={() => setStoryConfig({ ...storyConfig, setting: setting.id })}
                >
                  <span className="card-icon">{setting.icon}</span>
                  <span>{setting.label.split(' ').slice(1).join(' ')}</span>
                </button>
              ))}
            </div>
          </div>
        )

      case 'conflict':
        return (
          <div className="step-content">
            <h2>{content.ui.whatProblem}</h2>
            <div className="options-list">
              {content.conflicts.map(conflict => (
                <button
                  key={conflict.id}
                  className={`option-btn full-width ${storyConfig.conflict === conflict.id ? 'selected' : ''}`}
                  onClick={() => setStoryConfig({ ...storyConfig, conflict: conflict.id })}
                >
                  {conflict.label}
                </button>
              ))}
            </div>
          </div>
        )

      case 'tone':
        return (
          <div className="step-content">
            <h2>{content.ui.howFeeling}</h2>
            <div className="options-grid emoji-grid">
              {content.tones.map(tone => (
                <button
                  key={tone.id}
                  className={`option-btn emoji ${storyConfig.tone === tone.id ? 'selected' : ''}`}
                  onClick={() => setStoryConfig({ ...storyConfig, tone: tone.id })}
                >
                  <span className="emoji-large">{tone.label}</span>
                  <span>{tone.name}</span>
                </button>
              ))}
            </div>
          </div>
        )

      case 'review':
        const prompt = buildPrompt()
        return (
          <div className="step-content review">
            <h2>{content.ui.yourPrompt}</h2>
            <div className="prompt-preview">
              <p>{content.ui.youPicked}</p>
              <p className="prompt-text">{prompt.display}</p>
            </div>
            <div className="teaching-moment">
              <p><strong>{content.ui.teachingMoment1}</strong></p>
              <p>{content.ui.teachingMoment2} <em>{content.ui.prompt}</em>! {content.ui.teachingMoment3}</p>
            </div>
            {error && <p className="error">{error}</p>}
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="ai-creator">
      <header className="creator-header">
        <button className="back-btn" onClick={onBack}>{content.ui.back}</button>
        <h1>{content.ui.createYourStory}</h1>
        <div className="step-indicator">
          {STEPS.map((s, i) => (
            <span key={s} className={`step-dot ${i <= step ? 'active' : ''}`} />
          ))}
        </div>
      </header>

      <main className="creator-main">
        {renderStep()}
      </main>

      <footer className="creator-footer">
        {step > 0 && (
          <button className="nav-btn" onClick={() => setStep(step - 1)}>
            {content.ui.back}
          </button>
        )}
        {step < STEPS.length - 1 ? (
          <button 
            className="nav-btn primary" 
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
          >
            {content.ui.next}
          </button>
        ) : (
          <button 
            className="nav-btn primary generate" 
            onClick={generateStory}
            disabled={isGenerating}
          >
            {isGenerating ? content.ui.generating : content.ui.generate}
          </button>
        )}
      </footer>
    </div>
  )
}

export default AICreator
